import { RootState } from 'app/store';
import DropDownButtonLegacy from 'common/components/drop-downs/legacy/dropDownButtonLegacy';
import SimpleButtonContainerContent from 'common/components/drop-downs/legacy/simpleDropDownButtonContainerContent';
import Textbox from 'common/components/inputs/textbox';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectConfigName,
  selectSegmentName,
  selectSegmentValue,
  selectSegmentationType,
  selectRuleAccessType,
  selectConfigNameErrors,
  selectSegmentNameErrors,
  onSegmentationConfigNameChanged,
  onSegmentNameChanged,
  onSegmentValueChanged,
  onSegmentationTypeChanged,
  onRuleAccessTypeChanged,
  onSegmentationConfigNameErrorsChanged,
  onSegmentNameErrorsChanged
} from '../segmentation-config.slice';
import { DropDownItemType } from 'common/components/drop-downs/legacy/dropDownItemType';
import {
  getRuleAccessType,
  getSegmentationType,
  RuleAccessType,
  SegmentationType
} from '../segmentation-config.types';

const configNameMinLength = 1;
const configNameMaxLength = 50;
const validateConfigName = (configName: string): string[] => {
  if (configName.length < configNameMinLength) {
    const errorMsg = `Config Name must be at least ${configNameMinLength} character`;
    return [errorMsg];
  }

  if (configName.length > configNameMaxLength) {
    return [`Config Name must be less than ${configNameMaxLength} characters`];
  }

  return [];
};

const segmentNameMinLength = 1;
const segmentNameMaxLength = 50;
const validateSegmentName = (segmentName: string): string[] => {
  if (segmentName.length < configNameMinLength) {
    return [`Segment Name must be at least ${segmentNameMinLength} character`];
  }

  if (segmentName.length > configNameMaxLength) {
    return [
      `Segment Name must be less than ${segmentNameMaxLength} characters`
    ];
  }

  return [];
};

interface SegmentationRuleConfigFormProps {
  segmentTypeDropDownItems: DropDownItemType[];
  ruleAccessTypeDropDownItems: DropDownItemType[];
}

const SegmentationRuleConfigForm = ({
  segmentTypeDropDownItems,
  ruleAccessTypeDropDownItems
}: SegmentationRuleConfigFormProps) => {
  const dispatch = useDispatch();

  const configName = useSelector((state: RootState) => selectConfigName(state));
  const segmentName = useSelector((state: RootState) =>
    selectSegmentName(state)
  );
  const segmentValue = useSelector((state: RootState) =>
    selectSegmentValue(state)
  );
  const selectedSegmentationType = useSelector((state: RootState) =>
    selectSegmentationType(state)
  );
  const selectedRuleAccessType = useSelector((state: RootState) =>
    selectRuleAccessType(state)
  );
  const configNameErrors = useSelector((state: RootState) =>
    selectConfigNameErrors(state)
  );
  const segmentNameErrors = useSelector((state: RootState) =>
    selectSegmentNameErrors(state)
  );

  const setConfigName = (newSegmentationConfigName: string) => {
    dispatch(onSegmentationConfigNameChanged({ newSegmentationConfigName }));
  };

  const setSegmentName = (newSegmentName: string) => {
    dispatch(onSegmentNameChanged({ newSegmentName }));
  };

  const setSegmentValue = (newSegmentValue: string) => {
    dispatch(onSegmentValueChanged({ newSegmentValue }));
  };

  const setSegmentationType = (newSegmentationType: SegmentationType) => {
    dispatch(onSegmentationTypeChanged({ newSegmentationType }));
  };

  const setRuleAccessType = (newRuleAccessType: RuleAccessType) => {
    dispatch(onRuleAccessTypeChanged({ newRuleAccessType }));
  };

  const setConfigNameErrors = (newSegmentationConfigNameErrors: string[]) => {
    dispatch(
      onSegmentationConfigNameErrorsChanged({ newSegmentationConfigNameErrors })
    );
  };

  const setSegmentNameErrors = (newSegmentNameErrors: string[]) => {
    dispatch(onSegmentNameErrorsChanged({ newSegmentNameErrors }));
  };

  return (
    <>
      <div className="lg:w-full">
        <label htmlFor="config-name" className="block mb-1 text-sm">
          Config Name
        </label>
        <Textbox
          identifier="config-name"
          errors={configNameErrors}
          maxCharacterLimit={configNameMaxLength}
          value={configName}
          onChange={(value) => {
            const errors = validateConfigName(value);
            setConfigNameErrors(errors);
            setConfigName(value);
          }}
        />
      </div>
      <div className="lg:w-full">
        <label htmlFor="segment-name" className="block mb-1 text-sm">
          Segment Name
        </label>
        <Textbox
          identifier="segment-name"
          errors={segmentNameErrors}
          value={segmentName}
          maxCharacterLimit={segmentNameMaxLength}
          onChange={(value) => {
            const errors = validateSegmentName(value);
            setSegmentNameErrors(errors);
            setSegmentName(value);
          }}
        />
      </div>
      <div className="lg:w-full">
        <label htmlFor="segment-value" className="block mb-1 text-sm">
          Segment Value
        </label>
        <Textbox
          identifier="segment-value"
          errors={[]}
          value={segmentValue ?? ''}
          onChange={(value) => {
            setSegmentValue(value);
          }}
        />
      </div>
      <div className="lg:w-108 mt-5">
        <label className="block mb-1 text-sm">Segmentation Type</label>
        <DropDownButtonLegacy
          dropDirection={'left'}
          dropDownItems={segmentTypeDropDownItems}
          selectDropDownItem={(value) => {
            const segmentationType = getSegmentationType(value);
            setSegmentationType(segmentationType);
          }}
        >
          <SimpleButtonContainerContent
            additionalContainerStyling="bg-white rounded border border-neutral-200 w-40"
            dropdownLabel={selectedSegmentationType}
          />
        </DropDownButtonLegacy>
      </div>
      <div className="lg:w-108 mt-5">
        <label className="block mb-1 text-sm">Rule Access Type</label>
        <DropDownButtonLegacy
          dropDirection={'left'}
          dropDownItems={ruleAccessTypeDropDownItems}
          selectDropDownItem={(value) => {
            const ruleAccessType = getRuleAccessType(value);
            setRuleAccessType(ruleAccessType);
          }}
        >
          <SimpleButtonContainerContent
            additionalContainerStyling="bg-white rounded border border-neutral-200 w-40"
            dropdownLabel={selectedRuleAccessType}
          />
        </DropDownButtonLegacy>
      </div>
    </>
  );
};

export default SegmentationRuleConfigForm;
